import { fromJS, Map } from "immutable";
import { Action, AnyAction } from "redux";
import { combineReducers } from "redux-immutable";
import { KlaviyoIntegrationList } from "./types";

const KlaviyoActionType = {
  SETUP: "integrations/klaviyo/setup",
  SELECT_LIST: "integrations/klaviyo/selectList",
  DELETE: "integrations/klaviyo/delete",
  GET: "integrations/klaviyo/get",
  RESET_REQUESTS: "integrations/klaviyo/resetRequests"
} as const;
type KlaviyoRequestAction = Action<(typeof KlaviyoActionType)[keyof typeof KlaviyoActionType]> & {
  shopId: number;
  meta: unknown;
} & Record<string, unknown>;

export type KlaviyoDataReduxState = {
  settings?: {
    lists: KlaviyoIntegrationList[];
    selectedListId?: string;
  };
};

type KlaviyoDataReduxStateImmutableState = Map<unknown, unknown>;
const initialImmutableDataState = fromJS({});

type RequestErrorState = "none" | "invalid-key" | "unknown-error";

export type KlaviyoRequestReduxState = {
  loading: boolean;
  success: boolean;
  error: RequestErrorState;
};
type KlaviyoRequestReduxImmutableState = Map<keyof KlaviyoRequestReduxState, unknown>;

const initialRequestState: KlaviyoRequestReduxState = {
  loading: false,
  success: false,
  error: "none"
};
const initialImmutableRequestState = fromJS(initialRequestState);

const dataReducer = (
  state: KlaviyoDataReduxStateImmutableState = initialImmutableDataState,
  action: AnyAction
): KlaviyoDataReduxStateImmutableState => {
  switch (action.type) {
    case KlaviyoActionType.GET:
      return state.merge(fromJS({ settings: action.data.settings }));
    case KlaviyoActionType.DELETE:
      return state.merge(fromJS({ settings: undefined }));
    default:
      return state;
  }
};

export const resetKlaviyoIntegrationsRequestsAction = (): AnyAction => {
  return {
    type: KlaviyoActionType.RESET_REQUESTS
  };
};

export const getKlaviyoIntegrationSettingsAction = (shopId: number): KlaviyoRequestAction => {
  return {
    type: KlaviyoActionType.GET,
    shopId,
    meta: {
      type: "api",
      url: `${shopId}/integrations/klaviyo`,
      method: "get"
    }
  };
};
const getReducer = (
  state = initialImmutableRequestState,
  action: AnyAction
): KlaviyoRequestReduxImmutableState => {
  switch (action.type) {
    case KlaviyoActionType.RESET_REQUESTS:
      return initialImmutableRequestState;
    case KlaviyoActionType.GET:
      return state.merge(
        fromJS({
          loading: false,
          success: true,
          error: "none"
        })
      );
    case `${KlaviyoActionType.GET}_LOADING`:
      return state.merge(
        fromJS({
          loading: true,
          success: false,
          error: "none"
        })
      );
    case `${KlaviyoActionType.GET}_ERROR`:
      return state.merge(
        fromJS({
          loading: false,
          success: false,
          error: "unknown-error"
        })
      );
    default:
      return state;
  }
};

export const deleteKlaviyoIntegrationAction = (shopId: number): KlaviyoRequestAction => {
  return {
    type: KlaviyoActionType.DELETE,
    shopId,
    meta: {
      type: "api",
      url: `${shopId}/integrations/klaviyo`,
      method: "delete",
      error: {
        400: "invalid-key"
      }
    }
  };
};
const deleteReducer = (
  state: KlaviyoRequestReduxImmutableState = initialImmutableRequestState,
  action: AnyAction
): KlaviyoRequestReduxImmutableState => {
  switch (action.type) {
    case KlaviyoActionType.RESET_REQUESTS:
      return initialImmutableRequestState;
    case KlaviyoActionType.DELETE:
      return state.merge(
        fromJS({
          loading: false,
          success: true,
          error: "none"
        })
      );
    case `${KlaviyoActionType.DELETE}_LOADING`:
      return state.merge(
        fromJS({
          loading: true,
          success: false,
          error: "none"
        })
      );
    case `${KlaviyoActionType.DELETE}_ERROR`:
      return state.merge(
        fromJS({
          loading: false,
          success: false,
          error: "unknown-error"
        })
      );
    default:
      return state;
  }
};

export const setupKlaviyoIntegrationAction = (
  shopId: number,
  apiKey: string
): KlaviyoRequestAction => {
  return {
    type: KlaviyoActionType.SETUP,
    shopId,
    meta: {
      type: "api",
      url: `${shopId}/integrations/klaviyo`,
      method: "post",
      data: {
        apiKey
      },
      error: {
        400: "invalid-key"
      }
    }
  };
};
const setupReducer = (
  state: KlaviyoRequestReduxImmutableState = initialImmutableRequestState,
  action: AnyAction
): KlaviyoRequestReduxImmutableState => {
  switch (action.type) {
    case KlaviyoActionType.RESET_REQUESTS:
      return initialImmutableRequestState;
    case KlaviyoActionType.SETUP:
      return state.merge(
        fromJS({
          loading: false,
          success: true,
          error: "none"
        })
      );
    case `${KlaviyoActionType.SETUP}_LOADING`:
      return state.merge(
        fromJS({
          loading: true,
          success: false,
          error: "none"
        })
      );
    case `${KlaviyoActionType.SETUP}_ERROR`:
      return state.merge(
        fromJS({
          loading: false,
          success: false,
          error: action.errorMessage === "invalid-key" ? "invalid-key" : "unknown-error"
        })
      );
    default:
      return state;
  }
};

export const selectKlaviyoListAction = (shopId: number, listId: string): KlaviyoRequestAction => {
  return {
    type: KlaviyoActionType.SELECT_LIST,
    shopId,
    meta: {
      type: "api",
      url: `${shopId}/integrations/klaviyo`,
      method: "patch",
      data: {
        listId
      }
    }
  };
};
const selectListReducer = (
  state: KlaviyoRequestReduxImmutableState = initialImmutableRequestState,
  action: AnyAction
): KlaviyoRequestReduxImmutableState => {
  switch (action.type) {
    case KlaviyoActionType.RESET_REQUESTS:
      return initialImmutableRequestState;
    case KlaviyoActionType.SELECT_LIST:
      return state.merge(
        fromJS({
          loading: false,
          success: true,
          error: "none"
        })
      );
    case `${KlaviyoActionType.SELECT_LIST}_LOADING`:
      return state.merge(
        fromJS({
          loading: true,
          success: false,
          error: "none"
        })
      );
    case `${KlaviyoActionType.SELECT_LIST}_ERROR`:
      return state.merge(
        fromJS({
          loading: false,
          success: false,
          error: "unknown-error"
        })
      );
    default:
      return state;
  }
};

const requestsReducer = combineReducers({
  get: getReducer,
  setup: setupReducer,
  delete: deleteReducer,
  selectList: selectListReducer
});

export const klaviyoIntegrationReducer = combineReducers({
  data: dataReducer,
  requests: requestsReducer
});
